import { ModalCloseButton } from "./ModalCloseButton";
import { Children, isValidElement, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../i18n/I18nProvider";
import "./StandardModal.css";

export function StandardModal({
  title,
  eyebrow,
  titleAside,
  body,
  onClose,
  children,
  footer,
  className = ""
}: {
  title: string;
  eyebrow?: string;
  titleAside?: ReactNode;
  body?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const { t } = useI18n();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  if (typeof document === "undefined") return null;

  const bodyChildren = Children.toArray(children);
  const lastChild = bodyChildren[bodyChildren.length - 1];
  const trailingActions = !footer && isValidElement<{ className?: string }>(lastChild)
    && /(?:^|\s)actions(?:\s|$)/.test(lastChild.props.className || "") ? bodyChildren.pop() : undefined;
  const actionFooter = footer || trailingActions;

  return createPortal(
    <div
      className="standard-modal-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section className={`standard-modal ${className}`.trim()} role="dialog" aria-modal="true" aria-label={title} onPointerDown={(event) => event.stopPropagation()}>
        <header className="standard-modal-header">
          <div className="standard-modal-heading">
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            <h2>{title}</h2>
            {body && <p>{body}</p>}
          </div>
          {titleAside && <div className="standard-modal-title-aside">{titleAside}</div>}
          <ModalCloseButton onClose={onClose}/>
        </header>
        {bodyChildren.length > 0 && <div className="standard-modal-body">{bodyChildren}</div>}
        {actionFooter && <footer className="standard-modal-footer">{actionFooter}</footer>}
      </section>
    </div>,
    document.body
  );
}


export function ConfirmModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  busy = false,
  danger = false
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
  danger?: boolean;
}) {
  const { t } = useI18n();
  return (
    <StandardModal title={title} body={body} onClose={onClose} className="confirm-modal">
      <div className="actions confirm-modal-actions">
        <button className="button secondary" type="button" onClick={onClose} disabled={busy}>{t("common.cancel")}</button>
        <button className={danger ? "button danger" : "button primary"} type="button" onClick={onConfirm} disabled={busy}>{busy ? t("common.loading") : confirmLabel}</button>
      </div>
    </StandardModal>
  );
}
